import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../firebase';
import { useAuth } from './AuthContext';
import { doc, onSnapshot } from 'firebase/firestore';
import { CheckCircle, LinkIcon, AlertTriangle, Loader2 } from 'lucide-react';

interface GoogleCalendarSettingsProps {}

const GoogleCalendarSettings: React.FC<GoogleCalendarSettingsProps> = () => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Listener em tempo real para verificar o estado da conexão
  useEffect(() => {
    if (!user?.uid) {
        setIsLoading(false);
        return;
    }

    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const userData = docSnapshot.data();
          // A conexão é válida se existir um refresh_token
          const hasValidConnection = !!userData?.googleCalendarToken?.refreshToken;
          setIsConnected(hasValidConnection);
        } else {
          setIsConnected(false);
        }
        setIsLoading(false);
      },
      (err) => {
        console.error('Erro ao verificar estado da conexão:', err);
        setError('Não foi possível verificar o estado da conexão.');
        setIsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [user?.uid]);
  
  // Função para iniciar a conexão
  const handleConnect = async (): Promise<void> => {
    if (!user?.uid) {
      setError('Utilizador não autenticado');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // 1. Chama a Firebase Function para obter o URL de autenticação
      const googleAuthRedirect = httpsCallable(functions, 'googleAuthRedirect');
      const result = await googleAuthRedirect();
      const data = result.data as { authUrl?: string };

      if (!data?.authUrl) {
        throw new Error('URL de autorização não foi retornado pelo servidor.');
      }

      // 2. Redireciona para o fluxo de autenticação do Google
      window.location.assign(data.authUrl);
      
    } catch (err: any) {
      console.error('Erro ao iniciar conexão com Google:', err);
      setError(err.message || 'Ocorreu um erro ao tentar conectar.');
      setIsLoading(false);
    }
  };

  const handleDisconnect = async (): Promise<void> => {
    if (!user?.uid) {
      setError('Utilizador não autenticado');
      return;
    }

    // Confirmar se o usuário realmente quer desconectar
    const confirmDisconnect = window.confirm(
      'Tem a certeza que pretende desconectar a sua conta do Google Agenda? Esta ação irá remover a sincronização dos seus horários.'
    );

    if (!confirmDisconnect) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Chama a Firebase Function para revogar os tokens e limpar os dados
      const googleDisconnect = httpsCallable(functions, 'googleDisconnect');
      await googleDisconnect();

      // O listener em tempo real irá atualizar automaticamente o estado
      // quando os dados forem removidos do Firestore
      
    } catch (err: any) {
      console.error('Erro ao desconectar Google Calendar:', err);
      setError(err.message || 'Ocorreu um erro ao tentar desconectar.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
      return (
        <div className="bg-gray-50 p-4 rounded-lg border flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            <span>A verificar estado da conexão...</span>
        </div>
      )
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <LinkIcon className="w-5 h-5" />
        Integração com Google Agenda
      </h2>
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
                <p className="text-red-800 text-sm font-medium">Erro de Conexão</p>
                <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
        </div>
      )}
      {isConnected ? (
        <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div>
                    <p className="text-green-800 font-medium">Conexão Ativa</p>
                    <p className="text-green-700 text-sm">A sua agenda está sincronizada.</p>
                </div>
            </div>
            <button 
                onClick={handleDisconnect} 
                disabled={isLoading}
                className="text-sm text-gray-500 hover:text-red-600 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-1"
            >
                {isLoading ? (
                    <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        A desconectar...
                    </>
                ) : (
                    'Desconectar'
                )}
            </button>
        </div>
      ) : (
        <div className="space-y-4">
            <p className="text-gray-600">
              Para sincronizar os seus horários, conecte a sua conta do Google Agenda à plataforma.
            </p>
            <button
              onClick={handleConnect}
              disabled={isLoading}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all duration-200 bg-blue-600 hover:bg-blue-700 text-white shadow-sm disabled:bg-gray-400`}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  A redirecionar...
                </>
              ) : (
                <>
                  <LinkIcon className="w-4 h-4" />
                  Conectar com Google Agenda
                </>
              )}
            </button>
        </div>
      )}
    </div>
  );
};

export default GoogleCalendarSettings;
